/**
 * Broker API Client — Alpaca Integration
 * Typed functions for account, positions, orders, market data
 */

const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://trading-api-1075463475276.us-central1.run.app';

export interface BrokerAccount {
  account_number: string | null;
  status: string;
  equity: string;
  cash: string;
  buying_power: string;
  portfolio_value: string;
  long_market_value: string;
  short_market_value: string;
  last_equity: string;
  initial_margin: string;
  maintenance_margin: string;
  daytrade_count: number;
  daytrading_buying_power: string;
  pattern_day_trader: boolean;
  trading_blocked: boolean;
  transfers_blocked: boolean;
  account_blocked: boolean;
  currency: string;
  paper: boolean;
}

export interface BrokerPosition {
  symbol: string;
  qty: string;
  side: string;
  market_value: string;
  cost_basis: string;
  avg_entry_price: string;
  current_price: string;
  lastday_price: string;
  change_today: string;
  unrealized_pl: string;
  unrealized_plpc: string;
  unrealized_intraday_pl: string;
  unrealized_intraday_plpc: string;
  asset_id: string | null;
  asset_class: string;
}

export interface BrokerOrder {
  id: string;
  client_order_id: string | null;
  symbol: string;
  side: string | null;
  type: string | null;
  qty: string | null;
  filled_qty: string | null;
  filled_avg_price: string | null;
  limit_price: string | null;
  stop_price: string | null;
  trail_percent: string | null;
  trail_price: string | null;
  time_in_force: string | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
  submitted_at: string | null;
  filled_at: string | null;
  expired_at: string | null;
  canceled_at: string | null;
  notional: string | null;
  extended_hours: boolean;
}

export interface PlaceOrderRequest {
  symbol: string;
  side: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop';
  qty?: number;
  notional?: number;
  limit_price?: number;
  stop_price?: number;
  trail_percent?: number;
  trail_price?: number;
  time_in_force?: 'day' | 'gtc' | 'ioc' | 'fok';
  take_profit?: number;
  stop_loss?: number;
}

export interface PortfolioHistory {
  timestamps: number[];
  equity: number[];
  profit_loss: number[];
  profit_loss_pct: number[];
  base_value: number;
}

export interface BarData {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  vwap: number | null;
  trade_count: number | null;
}

export interface Watchlist {
  watchlist_id: string;
  user_id: string;
  name: string;
  symbols: string[];
  is_default: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserAlert {
  alert_id: string;
  user_id: string;
  symbol: string;
  condition: string;
  target_value: number;
  current_price_at_creation: number | null;
  channels: string[];
  status: string;
  triggered_at: string | null;
  triggered_price: number | null;
  note: string | null;
  created_at: string | null;
  expires_at: string | null;
}

export interface AutoTradeRule {
  rule_id: string;
  user_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  symbol: string | null;
  signal_type: string | null;
  min_confidence: number | null;
  max_risk_pct: number | null;
  min_growth_score: number | null;
  action_side: string;
  order_type: string;
  qty_type: string;
  qty_value: number;
  auto_stop_loss_pct: number | null;
  auto_take_profit_pct: number | null;
  max_daily_trades: number | null;
  max_daily_loss_usd: number | null;
  max_position_pct: number | null;
  cooldown_minutes: number | null;
  trades_today: number;
  total_trades: number;
  last_triggered: string | null;
  created_at: string | null;
}

async function fetchJSON(url: string, options?: RequestInit) {
  const res = await fetch(url, options);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export const brokerAPI = {
  // ─── Account ────────────────────────────────────
  getAccount: (): Promise<BrokerAccount> =>
    fetchJSON(`${API_BASE}/api/broker/account`),

  testConnection: () =>
    fetchJSON(`${API_BASE}/api/broker/test`),

  // ─── Positions ──────────────────────────────────
  getPositions: (): Promise<{ positions: BrokerPosition[]; count: number }> =>
    fetchJSON(`${API_BASE}/api/broker/positions`),

  closePosition: (symbol: string, percentage?: string) => {
    const url = percentage
      ? `${API_BASE}/api/broker/positions/${symbol}?percentage=${percentage}`
      : `${API_BASE}/api/broker/positions/${symbol}`;
    return fetchJSON(url, { method: 'DELETE' });
  },

  // ─── Orders ─────────────────────────────────────
  getOrders: (status = 'open', limit = 100): Promise<{ orders: BrokerOrder[]; count: number }> =>
    fetchJSON(`${API_BASE}/api/broker/orders?status=${status}&limit=${limit}`),

  placeOrder: (order: PlaceOrderRequest): Promise<{ success: boolean; order: BrokerOrder }> =>
    fetchJSON(`${API_BASE}/api/broker/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(order),
    }),

  cancelOrder: (orderId: string) =>
    fetchJSON(`${API_BASE}/api/broker/orders/${orderId}`, { method: 'DELETE' }),

  modifyOrder: (orderId: string, changes: Record<string, unknown>) =>
    fetchJSON(`${API_BASE}/api/broker/orders/${orderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changes),
    }),

  cancelAllOrders: () =>
    fetchJSON(`${API_BASE}/api/broker/orders/cancel-all`, { method: 'DELETE' }),

  // ─── Market Data ────────────────────────────────
  getBars: (symbol: string, timeframe = '1Day', limit = 100): Promise<{ symbol: string; bars: BarData[]; count: number }> =>
    fetchJSON(`${API_BASE}/api/broker/bars/${symbol}?timeframe=${timeframe}&limit=${limit}`),

  getQuote: (symbol: string) =>
    fetchJSON(`${API_BASE}/api/broker/quote/${symbol}`),

  // ─── Portfolio History ──────────────────────────
  getPortfolioHistory: (period = '1M', timeframe = '1D'): Promise<PortfolioHistory> =>
    fetchJSON(`${API_BASE}/api/broker/portfolio/history?period=${period}&timeframe=${timeframe}`),

  // ─── Watchlists ─────────────────────────────────
  getWatchlists: (userId = 'default'): Promise<{ watchlists: Watchlist[]; count: number }> =>
    fetchJSON(`${API_BASE}/api/watchlists?user_id=${userId}`),

  createWatchlist: (name: string, symbols: string[] = [], userId = 'default') =>
    fetchJSON(`${API_BASE}/api/watchlists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, symbols, user_id: userId }),
    }),

  updateWatchlist: (id: string, data: { name?: string; symbols?: string[] }) =>
    fetchJSON(`${API_BASE}/api/watchlists/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  deleteWatchlist: (id: string) =>
    fetchJSON(`${API_BASE}/api/watchlists/${id}`, { method: 'DELETE' }),

  addSymbolToWatchlist: (id: string, symbol: string) =>
    fetchJSON(`${API_BASE}/api/watchlists/${id}/symbols`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbol }),
    }),

  removeSymbolFromWatchlist: (id: string, symbol: string) =>
    fetchJSON(`${API_BASE}/api/watchlists/${id}/symbols/${symbol}`, { method: 'DELETE' }),

  // ─── Alerts ─────────────────────────────────────
  getAlerts: (userId = 'default', status = 'active'): Promise<{ alerts: UserAlert[]; count: number }> =>
    fetchJSON(`${API_BASE}/api/alerts?user_id=${userId}&status=${status}`),

  createAlert: (alert: { symbol: string; condition: string; target_value: number; channels?: string[]; current_price?: number; note?: string }) =>
    fetchJSON(`${API_BASE}/api/alerts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(alert),
    }),

  deleteAlert: (alertId: string) =>
    fetchJSON(`${API_BASE}/api/alerts/${alertId}`, { method: 'DELETE' }),

  getAlertStats: (userId = 'default') =>
    fetchJSON(`${API_BASE}/api/alerts/stats?user_id=${userId}`),

  // ─── Auto-Trade Rules ───────────────────────────
  getAutoTradeRules: (userId = 'default'): Promise<{ rules: AutoTradeRule[]; count: number }> =>
    fetchJSON(`${API_BASE}/api/auto-trade/rules?user_id=${userId}`),

  createAutoTradeRule: (rule: Partial<AutoTradeRule>) =>
    fetchJSON(`${API_BASE}/api/auto-trade/rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rule),
    }),

  updateAutoTradeRule: (ruleId: string, changes: Partial<AutoTradeRule>) =>
    fetchJSON(`${API_BASE}/api/auto-trade/rules/${ruleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changes),
    }),

  deleteAutoTradeRule: (ruleId: string) =>
    fetchJSON(`${API_BASE}/api/auto-trade/rules/${ruleId}`, { method: 'DELETE' }),

  getAutoTradeLog: (userId = 'default', limit = 50) =>
    fetchJSON(`${API_BASE}/api/auto-trade/log?user_id=${userId}&limit=${limit}`),

  killSwitch: (userId = 'default') =>
    fetchJSON(`${API_BASE}/api/auto-trade/kill-switch?user_id=${userId}`, { method: 'POST' }),
};

export default brokerAPI;
